import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@artosera/ui';
import { TableauDefilant } from '../../../../components/TableauDefilant.js';
import {
  estNaturePivot,
  lireTransformation,
  telechargerPivot,
  transformerLot,
  type NaturePivot,
} from '../../../../lib/correspondance.js';
import { morceauxLances } from '../../../../lib/reglesEdition.js';
import { lotEnTravail, type LotReprise } from '../../../../lib/reprise.js';
import pageStyles from '../../../../styles/page.module.css';
import tableStyles from '../../../../styles/table.module.css';
import { cleLots } from '../LotsBloc.js';
import { MorceauxEssai } from '../MorceauxEssai.js';
import { messageReprise } from '../messages.js';
import reprise from '../Reprise.module.css';
import styles from './Correspondance.module.css';

const ERREURS_AFFICHEES = 10;

/**
 * La transformation d'un lot : le profil et les surcharges deviennent des
 * morceaux de pivot de 5 000 lignes, un lot ordinaire chacun. Le rapport dit
 * ce qui a été lu, écarté, produit et refusé ; le pivot se télécharge en CSV ;
 * l'essai se fait morceau par morceau, et le lancement aussi.
 */
export function TransformationBloc({
  idGalerie,
  lot,
  peutAgir,
  onLot,
  onEssaiLance,
}: {
  idGalerie: string;
  lot: LotReprise;
  peutAgir: boolean;
  onLot: (lot: LotReprise) => void;
  onEssaiLance: () => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const client = useQueryClient();
  const [erreur, setErreur] = useState<string | null>(null);
  const transforme = lot.transformation?.fait === true;
  const enTravail =
    lotEnTravail(lot) || lot.travail?.etat === 'en_attente' || lot.travail?.etat === 'en_cours';
  const refuse = !enTravail && Boolean(lot.travail?.erreur);

  const rapport = useQuery({
    queryKey: [
      'console',
      'galerie',
      idGalerie,
      'reprise',
      'lot',
      lot.id,
      'transformation',
      lot.revision,
    ],
    queryFn: () => lireTransformation(idGalerie, lot.id),
    enabled: transforme,
  });
  const apres = (suite: LotReprise): void => {
    setErreur(null);
    onLot(suite);
    void client.invalidateQueries({ queryKey: cleLots(idGalerie) });
  };
  const echec = (cause: unknown): void => {
    setErreur(messageReprise(t, cause));
    void client.invalidateQueries({
      queryKey: ['console', 'galerie', idGalerie, 'reprise', 'lot', lot.id],
    });
  };
  const transformer = useMutation({
    mutationFn: () => transformerLot(idGalerie, lot.id),
    onSuccess: apres,
    onError: echec,
  });
  const telecharger = useMutation({
    mutationFn: (cible: { nature: NaturePivot; morceau?: number }) =>
      telechargerPivot(idGalerie, lot.id, cible),
    onError: echec,
  });

  const peutTransformer = peutAgir && lot.profil != null && !enTravail;
  const lances = morceauxLances(lot);

  return (
    <section className={pageStyles.panneau} aria-labelledby="corr-transformation">
      <div className={pageStyles.sectionTitre}>
        <h2 id="corr-transformation">{t('correspondance.transformation.titre')}</h2>
      </div>
      <div className={reprise.pile}>
        <p className={reprise.texte}>{t('correspondance.transformation.texte')}</p>
        {lot.profil ? (
          <p className={reprise.discret}>
            {t('correspondance.transformation.utilise', { version: lot.profil.version })}
          </p>
        ) : (
          <p className={reprise.avertissement} role="note">
            {t('correspondance.transformation.profilRequis')}
          </p>
        )}

        {erreur ? (
          <p className={reprise.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        {refuse && lot.travail?.erreur ? (
          <p className={reprise.erreur} role="alert">
            {t('correspondance.transformation.renonce', { message: lot.travail.erreur })}
          </p>
        ) : null}

        {peutAgir ? (
          <div className={reprise.actions}>
            <Button
              variant={transforme ? 'secondary' : 'primary'}
              loading={
                transformer.isPending || (enTravail && lot.travail?.type === 'reprise:transformer')
              }
              disabled={!peutTransformer}
              onClick={() => transformer.mutate()}
            >
              {transforme
                ? t('correspondance.transformation.transformerDeNouveau')
                : t('correspondance.transformation.transformer')}
            </Button>
          </div>
        ) : null}
        {enTravail && lot.travail?.type === 'reprise:transformer' ? (
          <p className={reprise.discret} aria-live="polite">
            {t('correspondance.transformation.enCours')}
          </p>
        ) : null}
        {transforme && lances === 0 ? (
          <p className={reprise.discret}>{t('correspondance.transformation.refaire')}</p>
        ) : null}
        {lances > 0 ? (
          <p className={reprise.avertissement} role="note">
            {t('correspondance.transformation.morceauxLances')}
          </p>
        ) : null}

        {transforme && rapport.isSuccess ? (
          <div className={reprise.rapport}>
            <h3>{t('correspondance.transformation.rapport')}</h3>
            <dl className={reprise.compteurs}>
              <div>
                <dt>{t('correspondance.transformation.lues')}</dt>
                <dd>{rapport.data.lus}</dd>
              </div>
              <div>
                <dt>{t('correspondance.transformation.filtrees')}</dt>
                <dd>{rapport.data.filtres}</dd>
              </div>
              <div>
                <dt>{t('correspondance.transformation.produites')}</dt>
                <dd>
                  {Object.values(rapport.data.natures).reduce(
                    (somme, nature) => somme + nature.produites,
                    0,
                  )}
                </dd>
              </div>
              <div data-refus={rapport.data.erreursTotal > 0 ? 'true' : undefined}>
                <dt>{t('correspondance.transformation.erreurs')}</dt>
                <dd>{rapport.data.erreursTotal}</dd>
              </div>
            </dl>

            <TableauDefilant libelle={t('correspondance.transformation.parNature')} empile>
              <table className={`${tableStyles.table} ${reprise.table}`}>
                <thead>
                  <tr>
                    <th scope="col">{t('correspondance.transformation.colNature')}</th>
                    <th scope="col">{t('correspondance.transformation.produites')}</th>
                    <th scope="col">{t('correspondance.apercu.doublons')}</th>
                    <th scope="col">{t('correspondance.apercu.vides')}</th>
                    <th scope="col">{t('correspondance.apercu.refusees')}</th>
                    <th scope="col">{t('correspondance.transformation.pivot')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(rapport.data.natures).map(([nature, bilan]) => (
                    <tr key={nature}>
                      <td data-label={t('correspondance.transformation.colNature')}>
                        {t(`reprise.natures.${nature}`)}
                      </td>
                      <td data-label={t('correspondance.transformation.produites')}>
                        {bilan.produites}
                      </td>
                      <td data-label={t('correspondance.apercu.doublons')}>{bilan.doublons}</td>
                      <td data-label={t('correspondance.apercu.vides')}>{bilan.vides}</td>
                      <td data-label={t('correspondance.apercu.refusees')}>{bilan.refusees}</td>
                      <td data-label={t('correspondance.transformation.pivot')}>
                        {estNaturePivot(nature) ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={
                              telecharger.isPending && telecharger.variables?.nature === nature
                            }
                            onClick={() => telecharger.mutate({ nature })}
                          >
                            {t('correspondance.transformation.telechargerPivot')}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableauDefilant>

            {rapport.data.erreurs.length > 0 ? (
              <div className={reprise.lignes}>
                <h4>
                  {t('correspondance.transformation.erreursTitre', {
                    count: rapport.data.erreursTotal,
                  })}
                </h4>
                <ul className={styles.erreursLignes}>
                  {rapport.data.erreurs.slice(0, ERREURS_AFFICHEES).map((ligne, rang) => (
                    <li key={rang}>
                      <span className={styles.erreurPosition}>
                        {t('correspondance.apercu.ligneSource', { ligne: ligne.ligne })}
                        {ligne.colonne ? ` · ${ligne.colonne}` : ''}
                      </span>
                      <span>{ligne.message}</span>
                    </li>
                  ))}
                </ul>
                {rapport.data.erreursTotal >
                Math.min(rapport.data.erreurs.length, ERREURS_AFFICHEES) ? (
                  <p className={reprise.discret}>
                    {t('correspondance.transformation.erreursGardees', {
                      count: rapport.data.erreurs.length,
                      total: rapport.data.erreursTotal,
                    })}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {transforme && rapport.isError ? (
          <p className={reprise.erreur} role="alert">
            {messageReprise(t, rapport.error) ?? t('correspondance.transformation.rapportErreur')}
          </p>
        ) : null}

        <MorceauxEssai
          idGalerie={idGalerie}
          lot={lot}
          peutAgir={peutAgir}
          actif={transforme}
          textes="correspondance"
          onLot={onLot}
          onEssaiLance={onEssaiLance}
          onTelecharger={(cible) => telecharger.mutate(cible)}
        />
      </div>
    </section>
  );
}
